
import type { CarouselItem, CarouselOptions, CarouselInterface, InstanceOptions, IndicatorItem } from 'flowbite';
import { Carousel } from 'flowbite'

import { formValidations as VALIDATIONS } from '../utils/form-validation';

const errorIcon = require("../../../../assets/svg/ui/error.svg") as string;

type HTMLFormFieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
type Targets = { [key: string]: HTMLElement };

export const otFormMultistep = {
    init() {
        const $forms = document.querySelectorAll('[data-ot-multistep-form]');
        $forms.forEach(($form, i) => {
            this.configureCarousel($form as HTMLElement, i);
        });
    },
    configureCarousel($form: HTMLElement, i: number) {
        const $targets =  this.getTargets($form as HTMLElement);
        const { carouselItems, $slideIndicators } = this.getCarouselItems($targets);

        $slideIndicators.forEach($slideIndicator => {
            $targets.slideIndicators.appendChild($slideIndicator);
        });

        const carouselOptions: CarouselOptions = {
            defaultPosition: 0,
            interval: -1,
            indicators: {
                activeClasses: 'bg-surface-accent',
                inactiveClasses: 'bg-border',
                items: Array.from($slideIndicators).map((indicator, i) => {
                    return { el: indicator, position: i } as IndicatorItem
                })
            },
            onChange: (e) => {
                this.changeSlides(e, $targets);
                this.toggleButtonStates(e, $targets);
            },
        };

        const instanceOptions: InstanceOptions = {
            id: 'carousel_' + i,
            override: false
        };

        const carousel: CarouselInterface = new Carousel(
            $targets.carousel as HTMLElement,
            carouselItems,
            carouselOptions,
            instanceOptions
        );

        this.bindUIEvents($targets, carousel);
    },
    getTargets($form: HTMLElement) {
        const $targets: Targets = {
            form: $form,
            formSuccess: $form.parentElement?.querySelector('[data-ot-form-success]') as HTMLElement,
            carousel: $form.querySelector('[data-carousel]') as HTMLElement,
            slidesWrapper: $form.querySelector('[data-ot-carousel-slides]') as HTMLElement,
            slideIndicators: $form.querySelector('[data-ot-slide-indicators]') as HTMLElement,
            submitButton: $form.querySelector('[data-ot-form-submit]') as HTMLButtonElement,
        };

        return $targets;
    },
    getCarouselItems($targets: Targets) {
        const carouselItems: CarouselItem[] = [];
        const $slideIndicators: HTMLElement[] = [];

        function createSlideIndicator(i: number) {
            // Don't use buttons since navigation should be limited to prev/next buttons
            const $slideIndicator = document.createElement('div');
            const $srText = document.createElement('span');

            $slideIndicator.classList.add('w-2', 'h-2', 'rounded-full', 'transition-colors', 'text-white', 'pointer-events-none');
            $srText.classList.add('sr-only');
            $srText.innerText = `Form Step ${i + 1}`;
            $slideIndicator.appendChild($srText);

            return $slideIndicator;
        }

        $targets.slidesWrapper?.querySelectorAll('.carousel-item')?.forEach(($slide, i) => {
            carouselItems?.push({ el: $slide as HTMLElement, position: i });
            $slideIndicators.push(createSlideIndicator(i));
        });

        return { carouselItems, $slideIndicators };
    },
    changeSlides(e: CarouselInterface, $targets: Targets) {
        const $currentSlide = e._activeItem.el;

        this.toggleButtonStates(e, $targets);

        // Toggle Slide States
        e._items.forEach((item) => {
            const $slide = item.el;
            const showClasses = $slide.getAttribute('data-show-classes')?.split(' ');
            const hideClasses = $slide.getAttribute('data-hide-classes')?.split(' ');

            if ($slide !== $currentSlide) { // Hide inactive slide
                showClasses?.length && $slide.classList.remove(...showClasses);
                hideClasses?.length && $slide.classList.add(...hideClasses);

                // Remove interactive child elements from tabbing order
                $slide.querySelectorAll<HTMLElement>('input, select, textarea, a, iframe').forEach($field => {
                    $field.tabIndex = -1;
                });

                function transitionEnd() {
                    $slide.removeEventListener("transitionend", transitionEnd);
                }

                $slide.addEventListener("transitionend", transitionEnd);
            } else { // Show active slide
                hideClasses?.length && $slide.classList.remove(...hideClasses);
                showClasses?.length && $slide.classList.add(...showClasses);

                // Add interactive child elements from tabbing order, focus on first one
                $slide.querySelectorAll<HTMLElement>('input, select, textarea, a, iframe').forEach(($field, i) => {
                    $field.removeAttribute('tabindex');

                    // Since changeSlides runs on initialization (page load), only set focus if active element is child of form
                    if (document.activeElement?.closest('[data-ot-multistep-form]') && i === 0) {
                        $field.focus();
                    }
                });
            }
        });
    },
    toggleButtonStates(e: CarouselInterface, $targets: Targets) {
        const $currentSlide = e._activeItem.el;
        const $nextBtn = $currentSlide.querySelector('[data-ot-next]');
        const $recaptcha = !!$currentSlide.querySelector('.g-recaptcha');

        if (!$currentSlide) {
            return;
        }

        if ($nextBtn) {
            $nextBtn.ariaDisabled = (!!this.validateFields($currentSlide).length).toString();
        }

        if ($targets?.submitButton) {
            $targets.submitButton.ariaDisabled = (!!this.validateFields($currentSlide).length || ($recaptcha && !window.recaptchaValid)).toString();
        }
    },
    bindUIEvents($targets: Targets, carousel: CarouselInterface) {
        const $prevButtons = $targets.form.querySelectorAll('[data-ot-prev]') as NodeListOf<HTMLButtonElement>;
        const $nextButtons = $targets.form.querySelectorAll('[data-ot-next]') as NodeListOf<HTMLButtonElement>;
        const $fields: NodeListOf<HTMLFormFieldElement> = $targets.form.querySelectorAll('input, select, textarea');

        let debounceTimer;

        $fields.forEach(($field: HTMLFormFieldElement) => {
            const $carouselItem = $field?.closest('.carousel-item');
            const $fieldset = $carouselItem?.querySelector('fieldset');
            const $nextBtn = $carouselItem?.querySelector('[data-ot-next]');

            if (!$fieldset) {
                return;
            }

            $field.addEventListener('input', (e) => {
                clearTimeout(debounceTimer);

                debounceTimer = setTimeout(() => {
                    if ($field.type === 'password') {
                        this.validateField(e.target, true);
                    }

                    this.toggleButtonStates(carousel, $targets);
                }, 100);
            });
        });

        $prevButtons.forEach($prevBtn => {
            $prevBtn.addEventListener('click', () => carousel.prev());
        });

        $nextButtons.forEach($nextBtn => {
            $nextBtn?.addEventListener('click', () => {
                const invalidFields = this.validateFields($nextBtn.closest('.carousel-item'), true);

                if (invalidFields.length < 1) {
                    carousel.next();
                }
            });
        });

        $targets.submitButton?.addEventListener('click', e => {
            e.preventDefault();

            if (this.handleSubmit($targets, carousel)) {
                // cancelable: true is required for Firefox to prevent submit
                $targets.form.dispatchEvent(new Event('submit', { cancelable: true })); 
            }
        });

        $targets.form.addEventListener('submit', (e) => {
            e.preventDefault();
        });

        document.addEventListener('grecaptcha-validated', e => {
            // Dispatched from recaptcha.ts
            window.recaptchaValid = true;
            this.toggleButtonStates(carousel, $targets)
        });
    },
    handleSubmit($targets: Targets, carousel: CarouselInterface) {
        const invalidFields = this.validateFields($targets.form, true);

        if (!invalidFields.length && $targets.submitButton.ariaDisabled !== 'true') {
            // Form is valid - handle success state
            return true;
        }

        const $form = $targets.form as HTMLFormElement;
        const $carouselSlide = $targets.form.querySelector(`[name="${invalidFields[0]}"]`)?.closest('.carousel-item');

        if ($carouselSlide === carousel?._activeItem.el) {
            $form.reportValidity();
            return;
        }

        // Go to slide containing first invalid field if it is not the current slide
        if ($carouselSlide?.parentNode?.children) {
            const slideIndex = Array.from($carouselSlide?.parentNode?.children).indexOf($carouselSlide);
            carousel?.slideTo(slideIndex);
        }

        const computedStyle = window.getComputedStyle(carousel._items[0].el);
        const transitionDuration = parseFloat(computedStyle.getPropertyValue('transition-duration')) * 1000;

        // Delay validity report by transition duration so input is visible, suppressing console error
        setTimeout(() => {
            $form.reportValidity();
        }, transitionDuration);
    },
    validateField($field: HTMLFormFieldElement, showFeedback?: boolean) {
        const fieldType = $field.nodeName.toLowerCase() === 'input' ? $field.type : $field.nodeName.toLowerCase();
        const validation = VALIDATIONS[fieldType];
        const invalidPatterns = validation?.invalidPatterns;
        const requiredPatterns = validation?.requiredPatterns;
        const matchPattern = validation?.matchPattern;
        const defaultMessage = validation?.defaultMessage;

        let errorMessage = ''; // Use error message as flag to represent validity

        if ((!$field.required && $field.value !== '') || $field.required) {
            // Value matches invalid patterns
            invalidPatterns?.forEach((invalidPattern) => {
                if ($field.value.match(invalidPattern.regExp)) {
                    errorMessage = invalidPattern.message;
                }
            });

            // Value does not match all required patterns
            if (requiredPatterns && !$field.dataset.matches) {
                requiredPatterns?.forEach((requiredPattern) => {
                    if (!$field.value.match(requiredPattern.regExp)) {
                        errorMessage += (' ' + requiredPattern.message) + '<br>';
                    }
                });
            }

            // Value does not match required pattern
            if (matchPattern && !$field.value.match(matchPattern)) {
                errorMessage = defaultMessage ?? "Invalid value provided";
            }

            // Value is missing
            if ($field.validity.valueMissing) {
                errorMessage = 'This field is required';
            }

            // Values must match
            if ($field.dataset.matches) {
                const $fieldToMatch = $field.closest('form')?.querySelector(`[name="${$field.dataset.matches}"]`) as HTMLFormFieldElement;

                if ($field.value !== $fieldToMatch.value) {
                    errorMessage = 'Passwords must match';
                }
            }
        }

        if (showFeedback) {
            this.handleErrorState($field, errorMessage);
        }

        return !!errorMessage;
    },
    validateFields($parent: HTMLElement, showFeedback?: boolean) {
        const HONEYPOT_FIELD_NAME = 'middle_name'; // Something that looks real

        // Collect all invalid field names
        let invalidFields: string[] = [];

        $parent?.querySelectorAll<HTMLFormFieldElement>('input, select, textarea').forEach($field => {
            if (this.validateField($field, showFeedback)) {
                invalidFields.push($field.name);
                return;
            } else {
                invalidFields = invalidFields.filter(fieldName => fieldName !== $field.name);
            }

            if ($field.name === HONEYPOT_FIELD_NAME && $field.value) {
                invalidFields.push($field.name);
            }
        });

        return invalidFields;
    },
    handleErrorState($field: HTMLFormFieldElement, errorMessage: string) {
        const DATA_INPUT_GROUP = 'data-ot-input-group';
        const DATA_INPUT_FOOTER = 'data-ot-input-footer';
        const DATA_INPUT_FOOTER_TEXT = 'data-ot-input-footer-text';

        const $inputGroup = $field.closest(`[${DATA_INPUT_GROUP}]`);

        if (errorMessage) {
            // Append input footer element if it does not exist, else just update its innerHTML
            if (!$inputGroup?.querySelector(`[${DATA_INPUT_FOOTER}]`)) {
                const $inputFooter = document.createElement('div');
                $inputFooter.classList.add('flex', 'items-center', 'gap-2', 'text-error', 'text-sm');
                $inputFooter.setAttribute(DATA_INPUT_FOOTER, '');

                const $inputFooterText = document.createElement('p');
                $inputFooterText.setAttribute(DATA_INPUT_FOOTER_TEXT, '');
                $inputFooterText.innerHTML = errorMessage;

                $inputFooter.innerHTML = errorIcon;
                $inputFooter.appendChild($inputFooterText);

                $inputGroup?.appendChild($inputFooter);
            } else {
                const $inputFooterText = $inputGroup?.querySelector(`[${DATA_INPUT_FOOTER_TEXT}]`);

                if ($inputFooterText) {
                    $inputFooterText.innerHTML = errorMessage;
                }
            }
        } else {
            $inputGroup?.querySelector(`[${DATA_INPUT_FOOTER}]`)?.remove();
        }
    }
}
